/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IStatus } from "../../../../interfaces/statuses";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { createStatus, listStatuses, showStatus, updateStatus } from "@/services";
import { entityTypes } from "@/types/entityTypes";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: IStatus = {
  id: '',
  name: '',
  order: 0,
  entityType: null,
  description: "",
  code: "",
  isOccurrence: false,
  occurrenceType: "",
  isStatusRestrictionEnabled: false,
  allowAssociateMeasurement: false,
  nextStatus: null,
  requiredFields: [],
  updateFieldWithDate: "",
  isDefault: false,
  statusChangeOnlyAllowed: false,
  isClosure: false,
  lockLinkedRecords: false,
  generateFinancialEntry: false,
  isInProgress: false,
  color: '#fffff',
  openApprovalFlow: false,
  idApprovalFlow: null,
  allowsRoutering: false,
  allowSettlement: false
};

export const useStatuses = ({ filter: filterInitial, disabledShowId, includes = [], disabledGetAll, defaultLimit, idService: idServiceInitial, id: idInitial }: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>,
  includes?: string[],
  idService?: string;
  id?: string | number;
}) => {
  const { id: idParam, idService: idServiceParam } = useParams();
  const id = idInitial || idParam;
  const idService = idServiceInitial || idServiceParam;
  const location = useLocation();
  const [statuses, setStatuses] = useState<IStatus[]>([]);
  const [status, setStatus] = useState<IStatus>(DEFAULT_DATA);

  const resetStatus = () => setStatus(DEFAULT_DATA);

  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleStatus = (key: string, value: any, custom?: (v: any) => any) => {
    setStatus((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit: limitP,
    filter,
    sort = { id: 'asc' }
  }: {
    page?: number;
    limit?: number;
    filter?: any
    sort?: any;
  }) => {
    setLoading(true);
    listStatuses({ includes: [...includes], filter, page, limit: limitP, sort })
      .then(({ statuses: listStatuses, limit: limitR, currentPage, totalItems }) => {
        setStatuses(listStatuses);
        
        setLimit(limitR);
        setTotalItems(totalItems)
      })
      .finally(() => setLoading(false));
  }, 500), []);

  const search = useEntitySearch({ list: listStatuses, key: "statuses", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showStatus({ id, })
      .then(({ status: StatusesShow }) => {
        setStatus(StatusesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = async () => {
    setLoading(true);
    try {
      const { status: statusCreate } = await createStatus({ status });
      toast.success(`Status criado com sucesso!`);
      // goEdit(statusCreate.id!);
      return statusCreate;
    } finally {
      setLoading(false);
    }
  };

  const update = async () => {
    setLoading(true);
    try {
      const { status: statusUpdate } = await updateStatus({ id: status.id!, status });
      toast.success(`Status atualizado com sucesso!`);
      // goEdit(statusUpdate.id!);
      return statusUpdate;
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort: sort })
  }, [page, limit, filter, sort])

  useEffect(() => {
    handleStatus('idService', idService);
    const isPage = location.pathname.includes('/towermanagement/statuses');

    if (id && (isPage || !disabledShowId)) {
      if (id !== "new") {
        get({ id: id });
      } else {
        resetStatus();
      }
      return;
    }
  }, [id]);

  return {
    statuses,
    status,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    handleStatus,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    entityTypes,
    resetStatus,
    getAll,
    handleSort,
  };
};
